import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UNIDADES, getUnidad } from "../lib/unidades";
import { Plus, Minus, Trash2, ShoppingCart, Calendar, Users, CheckCircle2, ArrowRight, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ===== Catálogo demo (fallback cuando el backend no devuelve nada para esa unidad) =====
const CATALOGO_DEMO = {
  petit: [
    { id: "p1", nombre: "Renta del salón principal (6h)", precio: 8000 },
    { id: "p2", nombre: "Hora extra", precio: 1200, unidad_medida: "hora" },
    { id: "p3", nombre: "Uso de alberca", precio: 1500 },
    { id: "p4", nombre: "Mobiliario premium", precio: 2500 },
    { id: "p5", nombre: "DJ instalado en salón", precio: 4500 },
  ],
  kermesita: [
    { id: "k1", nombre: "Decoración temática completa", precio: 6500 },
    { id: "k2", nombre: "Animación con personaje", precio: 3500 },
    { id: "k3", nombre: "Coordinación del evento", precio: 4000 },
    { id: "k4", nombre: "Mesa de dulces", precio: 3200 },
    { id: "k5", nombre: "Actividades para niños", precio: 2800 },
    { id: "k6", nombre: "Foto y video básico", precio: 5500 },
  ],
  bocatto: [
    { id: "b1", nombre: "Menú adulto · 3 tiempos", precio: 380, por: "persona" },
    { id: "b2", nombre: "Menú infantil", precio: 250, por: "persona" },
    { id: "b3", nombre: "Barra libre 5h", precio: 3500 },
    { id: "b4", nombre: "Coffee break", precio: 180, por: "persona" },
    { id: "b5", nombre: "Carrito de snacks", precio: 4200 },
    { id: "b6", nombre: "Postres y pastel", precio: 2800 },
    { id: "b7", nombre: "Meseros (por cada 15 invitados)", precio: 800 },
  ],
};

const $ = (n) => `$${(Math.round(n || 0)).toLocaleString("es-MX")}`;

const Pill = ({ unidad, children }) => {
  const u = getUnidad(unidad);
  return <span className="text-[10px] px-2 py-0.5 rounded-full font-medium" style={{ background: u.color_soft, color: u.color }}>{children}</span>;
};

const ContratarUnidad = () => {
  const { unidad } = useParams();
  const navigate = useNavigate();
  const unidadInicial = ["petit", "kermesita", "bocatto"].includes(unidad) ? unidad : "petit";
  const esCombinado = unidad === "combinado";

  // Pasos: 1 = Fecha · 2 = Servicios · 3 = Cliente y confirmar
  const [paso, setPaso] = useState(1);
  const [evento, setEvento] = useState({ fecha: "", hora_inicio: "17:00", hora_fin: "22:00", adultos: 30, ninos: 10, tipo: "cumpleanos", ubicacion: "" });
  const [items, setItems] = useState({ petit: [], kermesita: [], bocatto: [] });
  const [cliente, setCliente] = useState({ nombre: "", whatsapp: "", email: "", nombre_festejado: "" });
  const [anticipo, setAnticipo] = useState({ monto: 0, metodo: "transferencia", referencia: "" });
  const [tabCatalogo, setTabCatalogo] = useState(unidadInicial);
  const [submitting, setSubmitting] = useState(false);
  const [resultado, setResultado] = useState(null);

  const personas = (Number(evento.adultos) || 0) + (Number(evento.ninos) || 0);
  const subtotales = useMemo(() => {
    const calc = (arr) => arr.reduce((s, it) => s + (it.cantidad || 1) * (it.precio || 0), 0);
    return { petit: calc(items.petit), kermesita: calc(items.kermesita), bocatto: calc(items.bocatto) };
  }, [items]);
  const total = subtotales.petit + subtotales.kermesita + subtotales.bocatto;

  const agregar = (uSlug, prod) => {
    setItems(prev => {
      const existing = prev[uSlug].find(it => it.id === prod.id);
      if (existing) {
        return { ...prev, [uSlug]: prev[uSlug].map(it => it.id === prod.id ? { ...it, cantidad: it.cantidad + 1 } : it) };
      }
      const cantidadInicial = prod.por === "persona" ? Math.max(personas, 1) : 1;
      return { ...prev, [uSlug]: [...prev[uSlug], { ...prod, cantidad: cantidadInicial }] };
    });
  };

  const cambiarCantidad = (uSlug, idItem, delta) => {
    setItems(prev => ({
      ...prev,
      [uSlug]: prev[uSlug].map(it => it.id === idItem ? { ...it, cantidad: Math.max(1, it.cantidad + delta) } : it).filter(it => it.cantidad > 0),
    }));
  };

  const quitar = (uSlug, idItem) => {
    setItems(prev => ({ ...prev, [uSlug]: prev[uSlug].filter(it => it.id !== idItem) }));
  };

  const puedeContinuarPaso = (p) => {
    if (p === 1) return evento.fecha && personas > 0;
    if (p === 2) return total > 0;
    if (p === 3) return cliente.nombre && cliente.whatsapp;
    return true;
  };

  const enviarCarrito = async () => {
    if (!puedeContinuarPaso(3)) { toast.error("Completa nombre y WhatsApp del cliente"); return; }
    setSubmitting(true);
    try {
      const payload = {
        cliente: { ...cliente, tipo_evento: evento.tipo },
        evento: { ...evento, nombre: `${evento.tipo} · ${cliente.nombre}` },
        items_petit: items.petit.map(it => ({ nombre: it.nombre, cantidad: it.cantidad, precio: it.precio, servicio_id: it.id })),
        items_kermesita: items.kermesita.map(it => ({ nombre: it.nombre, cantidad: it.cantidad, precio: it.precio, servicio_id: it.id })),
        items_bocatto: items.bocatto.map(it => ({ nombre: it.nombre, cantidad: it.cantidad, precio: it.precio, servicio_id: it.id })),
        anticipo: anticipo.monto > 0 ? anticipo : undefined,
      };
      const r = await axios.post(`${API}/carrito/checkout`, payload);
      setResultado(r.data);
      toast.success("¡Tu evento se creó correctamente!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo procesar el carrito");
    } finally { setSubmitting(false); }
  };

  // ===== Pantalla resultado =====
  if (resultado) {
    return (
      <div className="min-h-screen p-6" style={{ background: "#fdf6ee" }}>
        <div className="max-w-2xl mx-auto bg-white rounded-3xl p-8 shadow-lg text-center">
          <CheckCircle2 className="w-20 h-20 mx-auto" style={{ color: "#16a34a" }} />
          <h1 className="font-display text-3xl mt-4" style={{ color: "var(--brand-text)" }}>¡Tu evento está apartado!</h1>
          <p className="text-sm mt-2" style={{ color: "var(--brand-text-muted)" }}>Hemos creado tu cliente, cotización, contrato y reservado la fecha automáticamente.</p>

          <div className="grid grid-cols-2 gap-3 mt-6 text-left">
            {resultado.unidades.includes("petit") && <Pill unidad="petit">{$(resultado.subtotales.petit)} Petit</Pill>}
            {resultado.unidades.includes("kermesita") && <Pill unidad="kermesita">{$(resultado.subtotales.kermesita)} Kermesita</Pill>}
            {resultado.unidades.includes("bocatto") && <Pill unidad="bocatto">{$(resultado.subtotales.bocatto)} Bocatto</Pill>}
          </div>

          <div className="mt-6 p-4 rounded-2xl" style={{ background: "var(--brand-primary-soft)" }}>
            <p className="text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--brand-primary)" }}>Total del evento</p>
            <p className="font-display text-4xl" style={{ color: "var(--brand-text)" }}>{$(resultado.total)}</p>
            {resultado.pago_id && <p className="text-xs mt-1" style={{ color: "var(--brand-success)" }}>Anticipo registrado · pago {resultado.pago_id}</p>}
          </div>

          <div className="grid grid-cols-2 gap-2 mt-4 text-xs text-left">
            <div className="p-2 rounded-lg" style={{ background: "var(--brand-surface-muted)" }}>
              <p className="text-[10px]" style={{ color: "var(--brand-text-muted)" }}>Evento</p>
              <p className="font-mono">{resultado.evento_id}</p>
            </div>
            <div className="p-2 rounded-lg" style={{ background: "var(--brand-surface-muted)" }}>
              <p className="text-[10px]" style={{ color: "var(--brand-text-muted)" }}>Contrato</p>
              <p className="font-mono">{resultado.contrato_id}</p>
            </div>
            {resultado.produccion_id && (
              <div className="p-2 rounded-lg col-span-2" style={{ background: "#ffedd5" }}>
                <p className="text-[10px]" style={{ color: "#9a3412" }}>Bocatto generó automáticamente la producción</p>
                <p className="font-mono text-[11px]">{resultado.produccion_id}</p>
              </div>
            )}
          </div>

          <div className="flex gap-2 mt-6">
            <button onClick={() => navigate(`/panel/eventos/${resultado.evento_id}`)} className="flex-1 py-3 rounded-xl text-white font-medium" style={{ background: "var(--brand-primary)" }} data-testid="btn-ir-evento">Ver expediente del evento</button>
            <button onClick={() => navigate("/seleccion")} className="flex-1 py-3 rounded-xl font-medium" style={{ background: "var(--brand-surface-muted)", color: "var(--brand-text)" }}>Otro evento</button>
          </div>
        </div>
      </div>
    );
  }

  // ===== Wizard =====
  const PASOS = [
    { n: 1, label: "Fecha y datos" },
    { n: 2, label: "Servicios" },
    { n: 3, label: "Cliente y pago" },
  ];

  return (
    <div className="min-h-screen" style={{ background: "#fdf6ee" }}>
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-display text-3xl" style={{ color: "var(--brand-text)" }}>{esCombinado ? "Evento combinado" : `Contratar ${getUnidad(unidadInicial)?.nombre || ""}`}</h1>
            <p className="text-sm" style={{ color: "var(--brand-text-muted)" }}>Arma tu evento en 3 pasos.</p>
          </div>
          <button onClick={() => navigate("/seleccion")} className="text-xs" style={{ color: "var(--brand-text-muted)" }}>← Cambiar selección</button>
        </div>

        {/* Stepper */}
        <div className="flex items-center gap-2 mb-6">
          {PASOS.map((p, i) => (
            <React.Fragment key={p.n}>
              <div className={`flex items-center gap-2 ${paso >= p.n ? "" : "opacity-40"}`}>
                <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium" style={{ background: paso >= p.n ? "var(--brand-primary)" : "var(--brand-surface-muted)", color: paso >= p.n ? "#fff" : "var(--brand-text)" }}>{p.n}</div>
                <span className="text-xs font-medium hidden sm:inline" style={{ color: "var(--brand-text)" }}>{p.label}</span>
              </div>
              {i < PASOS.length - 1 && <div className="flex-1 h-px" style={{ background: "var(--brand-border)" }} />}
            </React.Fragment>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr,360px] gap-5">
          <div className="space-y-4">
            {/* PASO 1: Fecha y datos */}
            {paso === 1 && (
              <div className="bg-white rounded-2xl p-5 space-y-3" data-testid="paso-fecha">
                <h2 className="font-display text-xl" style={{ color: "var(--brand-text)" }}>¿Cuándo y para cuántas personas?</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <label className="block">
                    <span className="text-xs" style={{ color: "var(--brand-text-muted)" }}>Fecha del evento</span>
                    <input type="date" value={evento.fecha} onChange={e => setEvento({ ...evento, fecha: e.target.value })} className="eo-input mt-1 w-full" data-testid="input-fecha" />
                  </label>
                  <label className="block">
                    <span className="text-xs" style={{ color: "var(--brand-text-muted)" }}>Tipo de evento</span>
                    <select value={evento.tipo} onChange={e => setEvento({ ...evento, tipo: e.target.value })} className="eo-input mt-1 w-full" data-testid="input-tipo">
                      <option value="cumpleanos">Cumpleaños</option>
                      <option value="primera_comunion">Primera Comunión</option>
                      <option value="bautizo">Bautizo</option>
                      <option value="xv">XV años</option>
                      <option value="boda">Boda</option>
                      <option value="baby_shower">Baby shower</option>
                      <option value="corporativo">Corporativo</option>
                      <option value="otro">Otro</option>
                    </select>
                  </label>
                  <label className="block">
                    <span className="text-xs" style={{ color: "var(--brand-text-muted)" }}>Hora inicio</span>
                    <input type="time" value={evento.hora_inicio} onChange={e => setEvento({ ...evento, hora_inicio: e.target.value })} className="eo-input mt-1 w-full" />
                  </label>
                  <label className="block">
                    <span className="text-xs" style={{ color: "var(--brand-text-muted)" }}>Hora fin</span>
                    <input type="time" value={evento.hora_fin} onChange={e => setEvento({ ...evento, hora_fin: e.target.value })} className="eo-input mt-1 w-full" />
                  </label>
                  <label className="block">
                    <span className="text-xs" style={{ color: "var(--brand-text-muted)" }}>Adultos</span>
                    <input type="number" min="0" value={evento.adultos} onChange={e => setEvento({ ...evento, adultos: e.target.value })} className="eo-input mt-1 w-full" data-testid="input-adultos" />
                  </label>
                  <label className="block">
                    <span className="text-xs" style={{ color: "var(--brand-text-muted)" }}>Niños</span>
                    <input type="number" min="0" value={evento.ninos} onChange={e => setEvento({ ...evento, ninos: e.target.value })} className="eo-input mt-1 w-full" data-testid="input-ninos" />
                  </label>
                  <label className="block md:col-span-2">
                    <span className="text-xs" style={{ color: "var(--brand-text-muted)" }}>Ubicación (si no es Celebra Petit)</span>
                    <input value={evento.ubicacion} onChange={e => setEvento({ ...evento, ubicacion: e.target.value })} placeholder="Celebra Petit / Domicilio / Salón propio…" className="eo-input mt-1 w-full" />
                  </label>
                </div>
              </div>
            )}

            {/* PASO 2: Servicios */}
            {paso === 2 && (
              <div className="bg-white rounded-2xl p-5" data-testid="paso-servicios">
                <h2 className="font-display text-xl mb-3" style={{ color: "var(--brand-text)" }}>Elige los servicios</h2>
                {/* Tabs por unidad */}
                <div className="flex gap-2 mb-4 border-b" style={{ borderColor: "var(--brand-border)" }}>
                  {UNIDADES.map(u => (
                    <button key={u.slug} onClick={() => setTabCatalogo(u.slug)} className="px-3 py-2 text-sm font-medium border-b-2 transition" style={{ borderColor: tabCatalogo === u.slug ? u.color : "transparent", color: tabCatalogo === u.slug ? u.color : "var(--brand-text-muted)" }} data-testid={`tab-${u.slug}`}>
                      {u.nombre}
                    </button>
                  ))}
                </div>
                {/* Catálogo de la unidad seleccionada */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {(CATALOGO_DEMO[tabCatalogo] || []).map(prod => {
                    const yaEnCarrito = items[tabCatalogo].find(it => it.id === prod.id);
                    return (
                      <div key={prod.id} className="p-3 rounded-xl flex items-center gap-3" style={{ background: "var(--brand-surface-muted)", border: yaEnCarrito ? `2px solid ${getUnidad(tabCatalogo).color}` : "2px solid transparent" }} data-testid={`catalogo-${tabCatalogo}-${prod.id}`}>
                        <div className="flex-1">
                          <p className="text-sm font-medium" style={{ color: "var(--brand-text)" }}>{prod.nombre}</p>
                          <p className="text-xs" style={{ color: "var(--brand-text-muted)" }}>{$(prod.precio)}{prod.por ? ` × ${prod.por}` : ""}</p>
                        </div>
                        <button onClick={() => agregar(tabCatalogo, prod)} className="w-9 h-9 rounded-lg flex items-center justify-center text-white" style={{ background: getUnidad(tabCatalogo).color }}><Plus className="w-4 h-4" /></button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* PASO 3: Cliente y anticipo */}
            {paso === 3 && (
              <div className="bg-white rounded-2xl p-5 space-y-3" data-testid="paso-cliente">
                <h2 className="font-display text-xl" style={{ color: "var(--brand-text)" }}>¿A nombre de quién?</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <label className="block">
                    <span className="text-xs" style={{ color: "var(--brand-text-muted)" }}>Nombre completo *</span>
                    <input value={cliente.nombre} onChange={e => setCliente({ ...cliente, nombre: e.target.value })} className="eo-input mt-1 w-full" data-testid="input-cliente-nombre" />
                  </label>
                  <label className="block">
                    <span className="text-xs" style={{ color: "var(--brand-text-muted)" }}>WhatsApp *</span>
                    <input value={cliente.whatsapp} onChange={e => setCliente({ ...cliente, whatsapp: e.target.value })} placeholder="999 123 4567" className="eo-input mt-1 w-full" data-testid="input-cliente-whatsapp" />
                  </label>
                  <label className="block">
                    <span className="text-xs" style={{ color: "var(--brand-text-muted)" }}>Email</span>
                    <input value={cliente.email} onChange={e => setCliente({ ...cliente, email: e.target.value })} className="eo-input mt-1 w-full" />
                  </label>
                  <label className="block">
                    <span className="text-xs" style={{ color: "var(--brand-text-muted)" }}>Nombre del festejado</span>
                    <input value={cliente.nombre_festejado} onChange={e => setCliente({ ...cliente, nombre_festejado: e.target.value })} className="eo-input mt-1 w-full" />
                  </label>
                </div>

                <div className="mt-3 p-4 rounded-xl" style={{ background: "var(--brand-primary-soft)" }}>
                  <p className="text-[10px] uppercase tracking-[0.18em] mb-2" style={{ color: "var(--brand-primary)" }}>Anticipo (opcional)</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    <input type="number" placeholder="Monto" value={anticipo.monto || ""} onChange={e => setAnticipo({ ...anticipo, monto: parseFloat(e.target.value) || 0 })} className="eo-input" data-testid="input-anticipo-monto" />
                    <select value={anticipo.metodo} onChange={e => setAnticipo({ ...anticipo, metodo: e.target.value })} className="eo-input">
                      <option value="transferencia">Transferencia</option>
                      <option value="efectivo">Efectivo</option>
                      <option value="tarjeta">Tarjeta</option>
                      <option value="deposito">Depósito</option>
                    </select>
                    <input placeholder="Referencia" value={anticipo.referencia} onChange={e => setAnticipo({ ...anticipo, referencia: e.target.value })} className="eo-input" />
                  </div>
                  <div className="flex gap-2 mt-2">
                    {[0.3, 0.5, 1].map(p => (
                      <button key={p} onClick={() => setAnticipo({ ...anticipo, monto: Math.round(total * p) })} className="text-xs px-2 py-1 rounded-lg" style={{ background: "var(--brand-surface)", color: "var(--brand-primary)" }}>
                        {p === 1 ? "Total" : `${p * 100}%`} · {$(total * p)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Navegación */}
            <div className="flex items-center justify-between">
              <button onClick={() => paso > 1 ? setPaso(paso - 1) : navigate("/seleccion")} className="eo-btn eo-btn-outline" data-testid="btn-anterior"><ArrowLeft className="w-4 h-4" /> Anterior</button>
              {paso < 3 ? (
                <button onClick={() => puedeContinuarPaso(paso) ? setPaso(paso + 1) : toast.error("Completa los datos requeridos")} className="eo-btn eo-btn-primary" data-testid="btn-siguiente">Siguiente <ArrowRight className="w-4 h-4" /></button>
              ) : (
                <button onClick={enviarCarrito} disabled={submitting || total === 0} className="eo-btn eo-btn-primary" data-testid="btn-confirmar">{submitting ? "Procesando…" : "Confirmar y apartar fecha"}</button>
              )}
            </div>
          </div>

          {/* SIDEBAR CARRITO MULTI-EMPRESA */}
          <aside className="bg-white rounded-2xl p-4 lg:sticky lg:top-4 h-fit" data-testid="carrito-resumen">
            <div className="flex items-center gap-2 mb-3">
              <ShoppingCart className="w-4 h-4" style={{ color: "var(--brand-primary)" }} />
              <p className="font-display text-lg" style={{ color: "var(--brand-text)" }}>Carrito</p>
            </div>
            {evento.fecha && (
              <div className="mb-3 p-2 rounded-lg flex items-center gap-2 text-xs" style={{ background: "var(--brand-surface-muted)" }}>
                <Calendar className="w-3.5 h-3.5" /> <span>{evento.fecha} · {evento.hora_inicio}–{evento.hora_fin}</span>
                <Users className="w-3.5 h-3.5 ml-1" /> <span>{personas}</span>
              </div>
            )}

            {UNIDADES.map(u => {
              const its = items[u.slug];
              if (its.length === 0) return null;
              return (
                <div key={u.slug} className="mb-3" data-testid={`carrito-${u.slug}`}>
                  <p className="text-[10px] uppercase tracking-[0.15em] mb-1 flex items-center justify-between" style={{ color: u.color }}>
                    <span>{u.nombre}</span>
                    <span>{$(subtotales[u.slug])}</span>
                  </p>
                  <div className="space-y-1">
                    {its.map(it => (
                      <div key={it.id} className="p-2 rounded-lg text-xs flex items-center gap-2" style={{ background: u.color_soft }}>
                        <div className="flex-1 min-w-0">
                          <p className="truncate" style={{ color: "var(--brand-text)" }}>{it.nombre}</p>
                          <p className="text-[10px]" style={{ color: "var(--brand-text-muted)" }}>{$(it.precio)} c/u</p>
                        </div>
                        <button onClick={() => cambiarCantidad(u.slug, it.id, -1)} className="w-5 h-5 rounded flex items-center justify-center" style={{ background: "#fff" }}><Minus className="w-3 h-3" /></button>
                        <span className="font-medium w-5 text-center">{it.cantidad}</span>
                        <button onClick={() => cambiarCantidad(u.slug, it.id, 1)} className="w-5 h-5 rounded flex items-center justify-center" style={{ background: "#fff" }}><Plus className="w-3 h-3" /></button>
                        <button onClick={() => quitar(u.slug, it.id)} className="w-5 h-5 rounded flex items-center justify-center" style={{ background: "#fff", color: "#dc2626" }}><Trash2 className="w-3 h-3" /></button>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}

            {total === 0 && <p className="text-xs text-center py-4" style={{ color: "var(--brand-text-muted)" }}>Tu carrito está vacío.<br />Avanza al paso 2 para agregar servicios.</p>}

            {total > 0 && (
              <div className="mt-3 pt-3 border-t" style={{ borderColor: "var(--brand-border)" }}>
                <div className="flex items-center justify-between font-display text-lg">
                  <span>Total</span>
                  <span style={{ color: "var(--brand-primary)" }}>{$(total)}</span>
                </div>
                {anticipo.monto > 0 && (
                  <>
                    <div className="flex items-center justify-between text-xs mt-1" style={{ color: "var(--brand-success)" }}>
                      <span>Anticipo</span><span>− {$(anticipo.monto)}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs font-medium mt-0.5">
                      <span>Saldo</span><span>{$(total - anticipo.monto)}</span>
                    </div>
                  </>
                )}
              </div>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
};

export default ContratarUnidad;
